import classNames from "classnames";
import { noop, uniqueId } from "lodash";
import * as React from "react";
import { useContext, useState } from "react";
import { TrackEventParams, useTrackEvent } from "~/common/audit";
import { semanticColors } from "~/common/colors/colors";
import { useEnterKeyHandler } from "~/sharedComponents/hooks/useKeyboardHandler";
import { Icon, IconNames } from "~/sharedComponents/icons";
import { Box, Text } from "~/sharedComponents/layout";
import { Popover } from "~/sharedComponents/popover";
import { EntityThumbnail } from "~/sharedComponents/thumbnails/EntityThumbnail";
import { ThumbnailData } from "~/sharedComponents/thumbnails/types";
import { TipIcon } from "~/sharedComponents/tooltip";
import * as styles from "./Menu.less";
import { MenuContext } from "./MenuContext";

export const MenuSeparator = () => <div className={styles.separator} />;

export type MenuItemProps = {
    text?: string;
    onClick?: () => void;
    icon?: IconNames;
    withEmptyIconOffset?: boolean;
    visible?: boolean;
    className?: string;
    auditEvent?: string | TrackEventParams;
    // ignoreClose: true -> popover stays opened after click
    ignoreClose?: boolean;
    disabled?: boolean;
    description?: string | React.ReactElement;
    dataTestId?: string;
    menuId?: string;
    isActive?: boolean;
    thumbnail?: ThumbnailData;
    wrapperRef?: React.RefObject<HTMLDivElement>;
};
export const MenuItem: React.FC<MenuItemProps> = props => {
    const {
        text,
        icon,
        className,
        ignoreClose,
        children,
        withEmptyIconOffset,
        visible,
        disabled,
        description,
        dataTestId,
        isActive,
        thumbnail,
        wrapperRef,
    } = props;

    const { onMouseMove, onMouseLeave } = useTrackHoveredMenuItem(props);
    const trackEvent = useTrackEvent();

    const onClick = (e?: React.MouseEvent) => {
        if (props.disabled) {
            e.stopPropagation();
            return;
        }

        props.onClick();

        if (props.auditEvent) {
            trackEvent(props.auditEvent);
        }
    };

    const onKeyUp = useEnterKeyHandler(() => onClick());

    return (
        visible !== false && (
            <>
                <div
                    ref={wrapperRef}
                    className={classNames(
                        styles.menu_item,
                        isActive && styles.opened,
                        !ignoreClose && Popover.CLOSE_CLASS,
                        disabled && styles.disabled,
                        className
                    )}
                    onClick={onClick}
                    onKeyUp={onKeyUp}
                    tabIndex={disabled ? undefined : 0}
                    data-testid={dataTestId}
                    onMouseMove={onMouseMove}
                    onMouseLeave={onMouseLeave}
                    role={"menuitem"}
                >
                    {icon && (
                        <Box color={disabled && semanticColors.neutral.icon.quaternary} marginRight={8}>
                            <Icon iconName={icon} className={!ignoreClose && Popover.CLOSE_CLASS} />
                        </Box>
                    )}
                    {thumbnail && (
                        <Box color={disabled && semanticColors.neutral.icon.quaternary} marginRight={8}>
                            <EntityThumbnail item={thumbnail} size={16} />
                        </Box>
                    )}
                    {text && (
                        <Text
                            className={classNames(!ignoreClose && Popover.CLOSE_CLASS)}
                            truncate={true}
                            title={text}
                            color={disabled && semanticColors.neutral.text.quaternary}
                            paddingLeft={withEmptyIconOffset && !icon && 24} // 16(icon) + 8(indent)
                        >
                            {text}
                        </Text>
                    )}
                    {description && <TipIcon>{description}</TipIcon>}
                    {children}
                </div>
                <p className={styles.menu_item_marker} />
            </>
        )
    );
};

MenuItem.defaultProps = {
    visible: true,
    onClick: noop,
};

const useTrackHoveredMenuItem = (props: MenuItemProps) => {
    const [id] = useState(() => props.menuId ?? uniqueId("menuitem_"));

    const { setHoveredMenuItem } = useContext(MenuContext);
    const onMouseMove: React.MouseEventHandler = () => setHoveredMenuItem(id);
    const onMouseLeave: React.MouseEventHandler = () => setHoveredMenuItem(null);

    return { onMouseMove, onMouseLeave };
};
